import React, { useState, useEffect } from 'react';
import { Plus, Trash2, KeyRound } from 'lucide-react';
import API_URL from '../../config';
import { Typography, TextField, Button, TableContainer, Table, TableHead, TableRow, TableBody, TableCell, IconButton, Alert } from './TailwindMuiStubs';

export default function UserManagement() {
    const [users, setUsers] = useState([]);
    const [snack, setSnack] = useState(null);
    
    const [newUsername, setNewUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    
    const [changingPasswordFor, setChangingPasswordFor] = useState(null);
    const [changePasswordVal, setChangePasswordVal] = useState('');

    const fetchUsers = () => {
        fetch(`${API_URL}/users`, { credentials: 'include' })
            .then(r => r.json())
            .then(data => {
                if(Array.isArray(data)) setUsers(data);
            })
            .catch(() => {});
    };

    useEffect(() => { fetchUsers(); }, []);

    const showSnack = (msg, severity = 'success') => {
        setSnack({ msg, severity });
        setTimeout(() => setSnack(null), 3000);
    };

    const handleAddUser = async () => {
        if (!newUsername || !newPassword) return showSnack('Vyplňte používateľské meno a heslo', 'error');
        try {
            const res = await fetch(`${API_URL}/users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: newUsername, password: newPassword }),
                credentials: 'include'
            });
            if (!res.ok) {
                const data = await res.json();
                return showSnack(data.error || 'Nastala chyba', 'error');
            }
            showSnack('Používateľ pridaný!');
            setNewUsername('');
            setNewPassword('');
            fetchUsers();
        } catch (e) {
            showSnack('Nastala chyba spojenia', 'error');
        }
    };

    const handleDeleteUser = async (id) => {
        if (!window.confirm("Naozaj vymazať tohto administrátora?")) return;
        try {
            const res = await fetch(`${API_URL}/users/${id}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (!res.ok) return showSnack('Chyba pri mazaní', 'error');
            showSnack('Používateľ vymazaný');
            fetchUsers();
        } catch (e) {
            showSnack('Chyba spojenia', 'error');
        }
    };

    const handleChangePassword = async (id) => {
        if (!changePasswordVal) return showSnack('Vyplňte nové heslo', 'error');
        try {
            const res = await fetch(`${API_URL}/users/${id}/password`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newPassword: changePasswordVal }),
                credentials: 'include'
            });
            if (!res.ok) return showSnack('Chyba pri zmene hesla', 'error');
            showSnack('Heslo úspešne zmenené!');
            setChangingPasswordFor(null);
            setChangePasswordVal('');
        } catch (e) {
            showSnack('Chyba spojenia', 'error');
        }
    };

    return (
        <div className="h-full">
            <div className="mb-8">
                <Typography variant="h4">Správa Používateľov</Typography>
                <Typography variant="body2">
                    Zoznam administrátorov, ktorí majú prístup do tohto panelu.
                </Typography>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2">
                    <Typography variant="h6">Existujúci administrátori</Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Prihlasovacie meno</TableCell>
                                    <TableCell>Akcie</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map(u => (
                                    <TableRow key={u.id}>
                                        <TableCell>{u.id}</TableCell>
                                        <TableCell><span className="font-bold text-[#1E3A8A]">{u.username}</span></TableCell>
                                        <TableCell>
                                            {changingPasswordFor === u.id ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="w-32">
                                                        <TextField fullWidth value={changePasswordVal} onChange={e => setChangePasswordVal(e.target.value)} type="password" />
                                                    </div>
                                                    <Button onClick={() => handleChangePassword(u.id)}>Uložiť</Button>
                                                    <Button variant="outlined" onClick={() => setChangingPasswordFor(null)}>X</Button>
                                                </div>
                                            ) : (
                                                <div className="flex gap-2">
                                                    <IconButton onClick={() => setChangingPasswordFor(u.id)} color="primary"><KeyRound size={18}/></IconButton>
                                                    <IconButton onClick={() => handleDeleteUser(u.id)} color="error"><Trash2 size={18}/></IconButton>
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>

                <div>
                    <Typography variant="h6">Pridať administrátora</Typography>
                    <div className="bg-white p-6 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-gray-200/60">
                        <TextField label="Prihlasovacie meno" value={newUsername} onChange={e => setNewUsername(e.target.value)} fullWidth />
                        <TextField label="Heslo" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} fullWidth sx={{mb:4}} />
                        <Button fullWidth onClick={handleAddUser} startIcon={<Plus size={18}/>}>Vytvoriť účet</Button>
                    </div>
                </div>
            </div>

            {snack && (
                <div className="fixed bottom-6 right-6 z-[9999] animate-in fade-in slide-in-from-bottom-4">
                    <Alert severity={snack.severity}>{snack.msg}</Alert>
                </div>
            )}
        </div>
    );
}
